import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Slider } from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import Box from '@mui/material/Box';
import { format, addMinutes, differenceInDays, parseISO } from 'date-fns';
import './slider_forecast_metric_00625.css';

const TimeSliderForecastMetric_00625 = ({ 
  initialDate, 
  onChange, 
  cqlFechaMin, 
  issueTime 
}) => {
  const TOTAL_STEPS = 95; 
  const STEP_MINUTES = 15;

  // 1. CALCULAR COMPORTAMIENTO (Memoizado antes del estado)
  const sliderBehavior = useMemo(() => {
    // Si faltan datos, devolvemos default
    if (!cqlFechaMin || !issueTime) return { 
      initialValue: 0, minValue: 0, maxValue: TOTAL_STEPS - 1, disabled: false, blockedPositions: [], dayDifference: null, issueHourSteps: 0
    };
    
    try {
      const cqlDate = parseISO(cqlFechaMin);
      const issueDate = parseISO(issueTime);
      
      const cqlDateMidnight = new Date(cqlDate.getFullYear(), cqlDate.getMonth(), cqlDate.getDate());
      const issueDateMidnight = new Date(issueDate.getFullYear(), issueDate.getMonth(), issueDate.getDate());
      const dayDifference = differenceInDays(cqlDateMidnight, issueDateMidnight);
      
      // Hora actual en pasos de 15 min (ej: las 10:00 son el paso 40)
      const issueHourSteps = issueDate.getHours() * 4; 
      
      let initialValue = 0;
      let blockedPositions = [];
      
      switch (dayDifference) {
        case 0: // D-0: Bloquear PASADO. El slider empieza en la hora actual.
          initialValue = issueHourSteps;
          blockedPositions = Array.from({ length: issueHourSteps }, (_, i) => i);
          break;
          
        case 1: // D-1: Libre. Empieza en 0.
          initialValue = 0;
          blockedPositions = [];
          break;
          
        case 2: // D-2: Bloquear FUTURO. Empieza en 0.
          initialValue = 0;
          const blockFromStep = issueHourSteps + (4 * 4); // +4 horas
          
          if (blockFromStep >= TOTAL_STEPS) {
             blockedPositions = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);
          } else {
             blockedPositions = Array.from({ length: TOTAL_STEPS - blockFromStep }, (_, i) => i + blockFromStep);
          }
          break;
          
        default:
          initialValue = 0;
          blockedPositions = [];
      }
      
      return { 
        initialValue, minValue: 0, maxValue: TOTAL_STEPS - 1, disabled: false, dayDifference, issueHourSteps, blockedPositions 
      };
    } catch (error) {
      console.error('Error calculating slider behavior:', error);
      return { initialValue: 0, minValue: 0, maxValue: TOTAL_STEPS - 1, disabled: false, blockedPositions: [] };
    }
  }, [cqlFechaMin, issueTime]);

  // 2. ESTADOS
  // 🔥 CORRECCIÓN 1: Inicializamos el slider DIRECTAMENTE donde toca (no en 0)
  // Al tener key única en el padre, esto asegura que nazca en la hora correcta (ej: 07:00 en D-0).
  const [sliderValue, setSliderValue] = useState(sliderBehavior.initialValue);
  const [startDate, setStartDate] = useState(new Date(cqlFechaMin));
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [adjustedDateTime, setAdjustedDateTime] = useState('');
  
  const previousIssueTime = useRef(issueTime);
  const previousCqlFechaMin = useRef(cqlFechaMin);

  // 3. GENERAR MARCAS (Cada Hora)
  const marks = useMemo(() => Array.from({ length: 25 }, (_, i) => {
    const stepIndex = i * 4; 
    const markDate = addMinutes(new Date(cqlFechaMin), stepIndex * 15);
    const isBlocked = sliderBehavior.blockedPositions.includes(stepIndex);
    return { 
      value: stepIndex, 
      label: format(markDate, 'H'), 
      blocked: isBlocked
    };
  }), [cqlFechaMin, sliderBehavior.blockedPositions]);

  const handlePlayButtonClick = () => { setIsAutoPlay(!isAutoPlay); setIsPlaying(!isPlaying); };

  const debounce = (func, wait) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  const debouncedOnChange = useCallback(debounce((selectedDate) => { onChange(selectedDate.toISOString()); }, 500), [onChange]);

  const handleSliderChange = (event, value) => {
    if (sliderBehavior.blockedPositions.includes(value)) return;
    setSliderValue(value);
    const selectedDate = addMinutes(new Date(cqlFechaMin), value * STEP_MINUTES);
    debouncedOnChange(selectedDate);
    setAdjustedDateTime(format(selectedDate, 'yyyy-MM-dd HH:mm:ss'));
  };

  // 4. EFECTO DE ACTUALIZACIÓN (Solo si cambian props sin desmontar)
  useEffect(() => {
    const issueTimeChanged = previousIssueTime.current !== issueTime;
    const cqlFechaMinChanged = previousCqlFechaMin.current !== cqlFechaMin;
    
    if ((issueTimeChanged || cqlFechaMinChanged) && cqlFechaMin) {
      setSliderValue(sliderBehavior.initialValue);
      const initialD = addMinutes(new Date(cqlFechaMin), sliderBehavior.initialValue * STEP_MINUTES);
      setAdjustedDateTime(format(initialD, 'yyyy-MM-dd HH:mm:ss'));
      onChange(initialD.toISOString());
      
      previousIssueTime.current = issueTime;
      previousCqlFechaMin.current = cqlFechaMin;
    }
  }, [issueTime, cqlFechaMin, sliderBehavior.initialValue, onChange]);

  // Autoplay
  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setSliderValue((prevValue) => {
          let nextValue = prevValue + 1; 
          while (nextValue <= sliderBehavior.maxValue && sliderBehavior.blockedPositions.includes(nextValue)) {
            nextValue++;
          }
          if (nextValue > sliderBehavior.maxValue) {
            nextValue = sliderBehavior.initialValue;
            while (sliderBehavior.blockedPositions.includes(nextValue) && nextValue <= sliderBehavior.maxValue) {
              nextValue++;
            }
          }
          const newDateTime = addMinutes(startDate, nextValue * STEP_MINUTES);
          debouncedOnChange(newDateTime);
          setAdjustedDateTime(format(newDateTime, 'yyyy-MM-dd HH:mm:ss'));
          return nextValue;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, startDate, debouncedOnChange, sliderBehavior]);

  useEffect(() => { setStartDate(new Date(cqlFechaMin)); }, [cqlFechaMin]);

  const getSliderClasses = () => {
    let classes = "forecast-metric-slider__slider";
    if (sliderBehavior.dayDifference === 0) classes += " forecast-metric-slider--d0";
    else if (sliderBehavior.dayDifference === 2) classes += " forecast-metric-slider--d2";
    return classes;
  };

  return (
    <div className="forecast-metric-slider">
      <div className="forecast-metric-slider__container">
        <button
          className={`forecast-metric-slider__button ${isAutoPlay ? 'forecast-metric-slider__button--stop' : 'forecast-metric-slider__button--play'}`}
          onClick={handlePlayButtonClick}
          type="button"
        >
          {isAutoPlay ? <StopCircleRoundedIcon className="forecast-metric-slider__icon" /> : <PlayCircleOutlineRoundedIcon className="forecast-metric-slider__icon" />}
        </button>

        <Box 
          className="forecast-metric-slider__wrapper"
          sx={{
            '--blocked-percentage': sliderBehavior.dayDifference === 0 
              ? `${(sliderBehavior.issueHourSteps / TOTAL_STEPS) * 100}%`
              : '0%' 
          }}
        >
          <Slider
            value={sliderValue}
            step={1} 
            marks={marks} 
            min={0}
            max={TOTAL_STEPS} 
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            // 🔥 CORRECCIÓN 2: Formato de Fecha Completa
            valueLabelFormat={(value) =>
              `${format(addMinutes(startDate, value * STEP_MINUTES), 'yyyy-MM-dd HH:mm')} UTC`
            }
            className={getSliderClasses()}
            classes={{
              root: 'forecast-metric-slider__root',
              rail: 'forecast-metric-slider__rail',
              track: 'forecast-metric-slider__track',
              thumb: 'forecast-metric-slider__thumb',
              mark: 'forecast-metric-slider__mark',
              markActive: 'forecast-metric-slider__mark--active',
              markLabel: 'forecast-metric-slider__mark-label',
              valueLabel: 'forecast-metric-slider__value-label',
            }}
          />
        </Box>
      </div>
    </div>
  );
};

export default TimeSliderForecastMetric_00625;